import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Eye, ShoppingBag, Star, Camera } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { seasonLabel } from "@/lib/seasons";
import { formatAUD } from "@/lib/media";
import SaleCountdown from "@/components/SaleCountdown";
import SneakPeekRemindDialog, { requestSneakPeekReminder, reminderToast } from "@/components/SneakPeekRemind";
import { toast } from "sonner";

const Badge = ({ children, color, text = "#19324A", testid }) => (
  <span data-testid={testid} className="px-2.5 py-1 rounded-full text-[11px] font-display font-bold shadow-sm" style={{ background: color, color: text }}>{children}</span>
);

function CardBadges({ product, soldOut, onSale, comingSoon }) {
  const season = seasonLabel(product.seasonal);
  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
      {comingSoon && <Badge color="#19324A" text="#FFD93D" testid={`card-coming-${product.slug}`}>COMING {comingSoon.toUpperCase()}</Badge>}
      {product.is_new && !comingSoon && <Badge color="#7ED957">NEW</Badge>}
      {season && <Badge color="#A78BFA" testid={`card-seasonal-${product.slug}`}>{season.emoji} {season.label}</Badge>}
      {product.best_seller && <Badge color="#4CC9F0">BEST SELLER</Badge>}
      {onSale && <Badge color="#FF6B9D" text="#fff">SALE</Badge>}
      {soldOut && <Badge color="#19324A" text="#fff">SOLD OUT</Badge>}
    </div>
  );
}

function CardRating({ product }) {
  if (!(product.review_count > 0)) return null;
  return (
    <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#19324A]/70" data-testid={`card-rating-${product.slug}`}>
      <Star className="w-3.5 h-3.5 fill-[#FFD93D] text-[#FFD93D]" /> {Number(product.rating_avg).toFixed(1)} <span className="text-[#19324A]/45">({product.review_count})</span>
      {product.photo_count > 0 && <span className="inline-flex items-center gap-0.5 ml-1 text-[#19324A]/55"><Camera className="w-3.5 h-3.5" /> {product.photo_count}</span>}
    </p>
  );
}

function CardPrice({ product, onSale }) {
  return (
    <>
      <div className="mt-3 flex items-center gap-2">
        <span className="font-display font-bold text-lg text-[#19324A]">{formatAUD(onSale ? product.sale_price : product.price)}</span>
        {onSale && <span className="text-sm text-[#19324A]/45 line-through">{formatAUD(product.price)}</span>}
      </div>
      {onSale && product.sale_ends_at && <div className="mt-1"><SaleCountdown ends={product.sale_ends_at} compact testid={`card-sale-countdown-${product.slug}`} /></div>}
    </>
  );
}

export default function ProductCard({ product, comingSoon = "" }) {
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [remindOpen, setRemindOpen] = useState(false);
  const soldOut = (product.stock ?? 0) <= 0;
  const onSale = !!product.sale_price;
  const needsChoice = (product.sizes?.length || 0) > 0 || (product.colours?.length || 0) > 0 || !!product.personalisation_label;
  const wished = isWishlisted(product.id);

  const handleHeart = () => {
    toggleWishlist(product.id, product.sale_price || product.price, product);
    if (!comingSoon || wished || !product.seasonal) return;
    if (user) { requestSneakPeekReminder({ email: user.email, product }).then(() => reminderToast(product)).catch(() => {}); }
    else setRemindOpen(true);
  };

  const handleAdd = () => {
    if (soldOut) return;
    if (needsChoice || comingSoon) { navigate(`/product/${product.slug}`); return; }
    addToCart(product, { quantity: 1 });
    toast.success(`${product.name} added to your bag 🐾`);
  };

  return (
    <div className="group relative bg-white rounded-3xl overflow-hidden soft-shadow card-hover flex flex-col" data-testid={`product-card-${product.slug}`}>
      {comingSoon && <SneakPeekRemindDialog open={remindOpen} onOpenChange={setRemindOpen} product={product} />}
      <CardBadges product={product} soldOut={soldOut} onSale={onSale} comingSoon={comingSoon} />
      <button onClick={handleHeart} aria-label={comingSoon ? "Remind me when it opens" : "Add to wishlist"} data-testid={`wishlist-${product.slug}`}
        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
        <Heart className={`w-5 h-5 ${wished ? "fill-[#FF6B9D] text-[#FF6B9D]" : "text-[#19324A]/60"}`} />
      </button>

      <Link to={`/product/${product.slug}`} className="block relative aspect-square bg-[#FFF9EE] overflow-hidden">
        <img src={(product.images || [])[0]} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-sm"><Eye className="w-4 h-4 text-[#19324A]" /></span>
      </Link>

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <Link to={`/product/${product.slug}`} className="font-display font-semibold text-[#19324A] leading-snug hover:text-[#FF6B9D] transition-colors line-clamp-2">{product.name}</Link>
        <CardRating product={product} />
        {product.colours?.length > 0 && (
          <div className="flex gap-1.5 mt-2" data-testid={`card-colours-${product.slug}`}>
            {product.colours.length === 1
              ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#4CC9F0]/15 text-[#19324A]/70 font-medium truncate">{product.colours[0]}</span>
              : <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#4CC9F0]/15 text-[#19324A]/70 font-medium whitespace-nowrap">{product.colours.length} colours</span>}
          </div>
        )}
        <CardPrice product={product} onSale={onSale} />
        <button onClick={handleAdd} disabled={soldOut} data-testid={`add-to-bag-${product.slug}`}
          className={`${comingSoon ? "btn-outline" : "btn-primary"} mt-4 w-full !py-2.5 !px-3 text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0`}>
          {comingSoon ? <Eye className="w-4 h-4 shrink-0" /> : <ShoppingBag className="w-4 h-4 shrink-0" />}
          {soldOut ? "Sold Out" : comingSoon ? "Sneak Peek" : needsChoice ? "Choose Options" : "Add to Bag"}
        </button>
      </div>
    </div>
  );
}
